import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Video, Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface VideoCallSectionProps {
  appointmentId: string;
  telehealthLink: string;
  userRole: 'admin' | 'practitioner' | 'clinic_staff';
  showJoinButton: boolean;
}

const VideoCallSection: React.FC<VideoCallSectionProps> = ({
  appointmentId,
  telehealthLink,
  userRole,
  showJoinButton,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleStartCall = () => {
    const routes = {
      admin: `/telehealth/call/${appointmentId}`,
      practitioner: `/practitioner-portal/telehealth/call/${appointmentId}`,
      clinic_staff: `/telehealth/call/${appointmentId}`,
    };
    navigate(routes[userRole]);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(telehealthLink);
      toast({
        title: 'Link Copied',
        description: 'Video call link has been copied to clipboard.',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to copy link. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-3 rounded-lg border border-green-100 bg-green-50 p-4'>
      <div className='flex items-center gap-2 text-green-700'>
        <Video className='h-5 w-5' />
        <span className='font-medium'>Video Call Ready</span>
      </div>

      <div className='flex gap-2'>
        {showJoinButton && (
          <Button type='button' variant='default' className='flex-1' onClick={handleStartCall}>
            <Video className='mr-2 h-4 w-4' />
            Start Video Call
          </Button>
        )}

        <Button
          type='button'
          variant='outline'
          onClick={handleCopyLink}
          className={showJoinButton ? '' : 'flex-1'}
        >
          <Copy className='h-4 w-4' />
        </Button>
      </div>

      <p className='text-xs text-gray-500'>Patient join link: {telehealthLink}</p>
    </div>
  );
};

export default VideoCallSection;

